import { v4 as uuidv4 } from 'uuid';
import { supabase, isSupabaseConfigured } from './supabase';
import { UserProgress } from '../types';

const USER_ID_KEY = 'frcsort-user-id';
const LOCAL_PROGRESS_KEY = 'frcsort-progress';

export const getOrCreateUserId = (): string => {
  const stored = localStorage.getItem(USER_ID_KEY);
  if (stored) return stored;
  const generated = uuidv4();
  localStorage.setItem(USER_ID_KEY, generated);
  return generated;
};

const toDate = (value?: string | null): Date => {
  if (!value) return new Date();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? new Date() : date;
};

const rowToProgress = (row: any): UserProgress => ({
  userId: row.user_id,
  flashcardId: row.flashcard_id,
  easeFactor: row.ease_factor ?? 2.5,
  interval: row.interval ?? 1,
  repetitions: row.repetitions ?? 0,
  nextReviewDate: toDate(row.next_review_date),
  lastReviewed: toDate(row.last_reviewed),
  quality: row.quality ?? 0,
  timesCorrect: row.times_correct ?? 0,
  timesIncorrect: row.times_incorrect ?? 0
});

const progressToRow = (progress: UserProgress) => ({
  user_id: progress.userId,
  flashcard_id: progress.flashcardId,
  ease_factor: progress.easeFactor,
  interval: progress.interval,
  repetitions: progress.repetitions,
  next_review_date: progress.nextReviewDate.toISOString(),
  last_reviewed: progress.lastReviewed.toISOString(),
  quality: progress.quality,
  times_correct: progress.timesCorrect,
  times_incorrect: progress.timesIncorrect
});

const localStorageKey = (userId: string) => `${LOCAL_PROGRESS_KEY}:${userId}`;

const loadLocalProgress = (userId: string): UserProgress[] => {
  try {
    const raw = localStorage.getItem(localStorageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as UserProgress[];
    return parsed.map((item) => ({
      ...item,
      nextReviewDate: toDate((item as any).nextReviewDate),
      lastReviewed: toDate((item as any).lastReviewed)
    }));
  } catch (error) {
    console.error('Failed to load local progress:', error);
    return [];
  }
};

const saveLocalProgress = (userId: string, progress: UserProgress[]) => {
  try {
    localStorage.setItem(localStorageKey(userId), JSON.stringify(progress));
  } catch (error) {
    console.error('Failed to save local progress:', error);
  }
};

export const loadUserProgress = async (userId: string): Promise<UserProgress[]> => {
  if (!isSupabaseConfigured || !supabase) {
    return loadLocalProgress(userId);
  }

  const { data, error } = await supabase
    .from('user_progress')
    .select('*')
    .eq('user_id', userId);

  if (error) {
    console.error('Failed to load Supabase progress, falling back to local:', error);
    return loadLocalProgress(userId);
  }

  return (data ?? []).map(rowToProgress);
};

export const persistUserProgress = async (userId: string, progress: UserProgress, allProgress: UserProgress[]) => {
  if (!isSupabaseConfigured || !supabase) {
    saveLocalProgress(userId, allProgress);
    return;
  }

  const { error } = await supabase
    .from('user_progress')
    .upsert(progressToRow(progress), {
      onConflict: 'user_id,flashcard_id'
    });

  if (error) {
    console.error('Failed to persist Supabase progress, caching locally:', error);
    saveLocalProgress(userId, allProgress);
  }
};

export const persistStudySession = async (userId: string, session: {
  technique: string;
  flashcardsStudied: number;
  correctAnswers?: number;
  totalTime: number;
}) => {
  if (!isSupabaseConfigured || !supabase) {
    return;
  }

  const { error } = await supabase
    .from('study_sessions')
    .insert({
      user_id: userId,
      start_time: new Date().toISOString(),
      end_time: new Date().toISOString(),
      flashcards_studied: session.flashcardsStudied,
      correct_answers: session.correctAnswers ?? 0,
      total_time: session.totalTime,
      technique: session.technique
    });

  if (error) {
    console.error('Failed to persist study session:', error);
  }
};
